/**
 * 浏览器兼容性检查组件
 * 在应用加载时检查浏览器兼容性，并显示警告信息
 */

import React, { useEffect, useState } from 'react';
import browserCompatibility from '../utils/browserCompatibility';
import { AlertTriangle, Chrome, Globe, Monitor, Info, CheckCircle2 } from 'lucide-react';

const BrowserCompatibilityChecker = ({ children }) => {
  const [compatibilityReport, setCompatibilityReport] = useState(null);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    // 检查浏览器兼容性
    const report = browserCompatibility.generateCompatibilityReport();
    setCompatibilityReport(report);
    
    // 打印兼容性报告到控制台
    browserCompatibility.logCompatibilityReport();
    
    // 如果不兼容，显示警告
    if (!report.compatible) {
      setShowWarning(true);
    }
  }, []);

  if (!compatibilityReport) {
    return children;
  }

  const isFirefox = compatibilityReport.browser.browserName === 'Firefox';

  return (
    <>
      <style>
        {`
          @keyframes slideIn {
            from {
              opacity: 0;
              transform: scale(0.95) translateY(10px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>
      {children}
      
      {/* 兼容性警告弹窗 */}
      {showWarning && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            animation: 'fadeIn 0.2s ease-out'
          }}
          onClick={() => setShowWarning(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '480px',
              width: '90%',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
              animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部图标 */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              alignItems: 'center', 
              marginBottom: '24px',
            }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#FFF4E5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '16px',
                color: '#FF9800'
              }}>
                <AlertTriangle size={32} strokeWidth={2} />
              </div>
              <h2 style={{ 
                margin: 0, 
                color: '#1a1a1a',
                fontSize: '22px',
                fontWeight: '700',
                textAlign: 'center'
              }}>
                建议更换浏览器
              </h2>
            </div>

            {/* 主要提示信息 */}
            <div style={{ 
              marginBottom: '24px',
              textAlign: 'center'
            }}>
              <p style={{ 
                margin: '0 0 12px 0', 
                fontSize: '15px', 
                lineHeight: '1.6',
                color: '#4a4a4a'
              }}>
                检测到您当前使用的是 <span style={{ fontWeight: '600', color: '#1a1a1a', background: '#f5f5f5', padding: '2px 6px', borderRadius: '4px' }}>{compatibilityReport.browser.browserName}</span>
              </p>
              <p style={{ 
                margin: 0, 
                fontSize: '14px', 
                lineHeight: '1.6',
                color: '#666'
              }}>
                {isFirefox 
                  ? '为了确保面试过程中的音视频通话质量和AI功能的稳定性，我们强烈建议您切换至以下兼容性更好的浏览器。'
                  : '当前浏览器可能无法支持部分核心功能（如实时语音、AI辅助），建议切换浏览器以获得最佳体验。'
                }
              </p>
            </div>

            {/* 推荐浏览器 */}
            <div style={{ marginBottom: '32px' }}>
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                {/* Chrome - 主要推荐 */}
                <a 
                  href="https://www.google.cn/chrome/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  style={{ textDecoration: 'none' }}
                >
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#F8FAFC',
                    borderRadius: '16px',
                    border: '1px solid #E2E8F0',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = '#3B82F6';
                    e.currentTarget.style.backgroundColor = '#EFF6FF';
                    e.currentTarget.style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.backgroundColor = '#F8FAFC';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                  >
                    <div style={{ 
                      width: '40px', 
                      height: '40px', 
                      background: 'white',
                      borderRadius: '10px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}>
                      <Chrome size={24} color="#3B82F6" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontWeight: '600', 
                        fontSize: '16px',
                        color: '#1a1a1a',
                        marginBottom: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        Google Chrome
                        <span style={{
                          fontSize: '12px',
                          backgroundColor: '#DCFCE7',
                          color: '#166534',
                          padding: '2px 8px',
                          borderRadius: '100px',
                          fontWeight: '500'
                        }}>推荐</span>
                      </div>
                      <div style={{ fontSize: '13px', color: '#64748B' }}>
                        最佳性能与兼容性体验
                      </div>
                    </div>
                    <CheckCircle2 size={20} color="#166534" style={{ opacity: 0.5 }} />
                  </div>
                </a>

                {/* 其他浏览器 */}
                <div style={{ 
                  display: 'flex', 
                  gap: '12px' 
                }}>
                  <div style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <Globe size={16} color="#666" />
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>Microsoft Edge</span>
                  </div>
                  <div style={{
                    flex: 1,
                    padding: '12px',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #eee',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}>
                    <Monitor size={16} color="#666" />
                    <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>360极速浏览器</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 按钮 */}
            <button
              onClick={() => setShowWarning(false)}
              style={{
                width: '100%',
                backgroundColor: '#1a1a1a',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: '14px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 16px rgba(0, 0, 0, 0.15)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
              }}
            >
              我知道了，继续使用
            </button>
          </div>
        </div>
      )}
    </>
  );
};


export default BrowserCompatibilityChecker;

